import Component from '../../Component';

export interface AdvancedSelectOption {
	value: string | number;
	label: string;
}

export interface AdvancedSelectParams {
	/**
	 * Selections
	 */
	options: Array<AdvancedSelectOption>;

	defaultOptions?: Array<string | number> | false;

	className?: string;

	/**
	 * Allows for custom rendering of each option
	 */
	optionRenderer?: (option: AdvancedSelectOption) => string;

	/**
	 * Allows for custom rendering of the optionsList
	 */
	optionsListRenderer?: (
		options: AdvancedSelectParams['options'],
		optionRenderer?: AdvancedSelectParams['optionRenderer']
	) => string;

	/**
	 * Allows for custom rendering of the button
	 */
	buttonRenderer?: () => string;

	/**
	 * Allows for multiple selection. FALSE by default
	 */
	multiple?: boolean;

	/**
	 * Allows for controlling the resulting selection
	 * on an option click
	 */
	onOptionClick?: (
		option: string,
		currentSelection: AdvancedSelectState['selectedOptions'],
		allOptions: AdvancedSelectParams['options']
	) => AdvancedSelectState['selectedOptions'];

	/**
	 * Allows for customized button label on selection change
	 */
	onButtonLabelUpdate?: (
		currentSelection: AdvancedSelectState['selectedOptions']
	) => string;
}

export interface AdvancedSelectState {
	selectedOptions: Array<AdvancedSelectOption>;
	expanded: boolean;
}

const defaultButtonRenderer = () => {
	return `
	<button class="advanced-select__button" data-button>
		<span data-button-label>Please Select</span>
		<i class="trevor-ti-caret-down icon"></i>
	</button>
`;
};

const defaultOptionRenderer = ({ value, label }: AdvancedSelectOption) => {
	return `
		<li class="advanced-select__option" data-value="${value}" data-option>
		${label}
		</li>
	`;
};

const defaultOptionsListRenderer = (
	options: AdvancedSelectOption[],
	optionRenderer: AdvancedSelectParams['optionRenderer'] = defaultOptionRenderer
) => {
	return `
			<ul class="advanced-select__options" data-options-list>
				${options.map(optionRenderer).join('')}
			</ul>
		`;
};

class AdvancedSelect extends Component<HTMLElement, AdvancedSelectState> {
	static selector = '.advanced-select';

	params: AdvancedSelectParams;

	constructor(element: HTMLElement, params: AdvancedSelectParams) {
		super(element);
		this.params = params;
	}

	state: AdvancedSelectState = {
		selectedOptions: [],
		expanded: false,
	};

	static children = {
		button: '[data-button]',
		buttonLabel: '[data-button-label]',
		list: '[data-options-list]',
		options: ['[data-option]'],
	};

	/**
	 * Render and initialize an AdvancedSelect instance.
	 *
	 * @param {AdvancedSelectParams} params
	 * @return AdvancedSelect
	 */
	static create(params: AdvancedSelectParams) {
		const {
			defaultOptions,
			options,
			buttonRenderer = defaultButtonRenderer,
			optionsListRenderer = defaultOptionsListRenderer,
			optionRenderer = defaultOptionRenderer,
		} = params;
		const initialOptions =
			Array.isArray(defaultOptions) && defaultOptions.length
				? options.filter(({ value }) => defaultOptions.includes(value))
				: defaultOptions === false
				? []
				: options.slice(0, 1);

		const element = Object.assign(document.createElement('div'), {
			className: ['advanced-select', params.className]
				.filter(Boolean)
				.join(' '),
		});

		// Render children
		element.insertAdjacentHTML(
			'beforeend',
			[
				// Render Button
				buttonRenderer(),
				// Render List,
				optionsListRenderer(options, optionRenderer),
			].join('')
		);

		const advancedSelect = AdvancedSelect.initializeWithElement(
			element,
			params
		);

		// Set initial state
		advancedSelect.setState({
			selectedOptions: initialOptions,
		});

		return advancedSelect;
	}

	afterInit() {
		this.children?.button.addEventListener(
			'click',
			this.onButtonClick.bind(this)
		);

		this.children?.options.forEach((option) =>
			option.addEventListener('click', this.onOptionClick.bind(this))
		);

		document.body.addEventListener('click', this.onBodyClick.bind(this));

		// Ensure each option is tabbable
		this.children?.options.forEach((element) =>
			element.setAttribute('tabindex', '0')
		);
	}

	onButtonClick(event: MouseEvent) {
		event.preventDefault();

		const { expanded } = this.state;

		this.setState({ expanded: !expanded });
	}

	onOptionClick(event: Event) {
		event.preventDefault();

		const optionValue =
			(event.currentTarget as HTMLElement).getAttribute('data-value') ||
			'';

		let selectedOptions = [];

		// Use provided onOoptionClick param if present
		if (typeof this.params.onOptionClick === 'function') {
			selectedOptions = this.params.onOptionClick(
				optionValue,
				this.state.selectedOptions,
				this.params.options
			);
		} else if (this.params.multiple) {
			// Handle multiple selection
			const isOptionSelected = this.state.selectedOptions.find(
				({ value }) => value === optionValue
			);

			// If currently selected, remove from selection
			if (isOptionSelected) {
				selectedOptions = this.state.selectedOptions.filter(
					({ value }) => value !== optionValue
				);
			} else {
				// Otherwise, add into selection
				selectedOptions = [
					...this.state.selectedOptions,
					this.params.options.find(
						({ value }) => value === optionValue
					) as AdvancedSelectOption,
				];
			}
		} else {
			selectedOptions = this.params.options.filter(
				({ value }) => String(value) === optionValue
			);
		}

		this.select(selectedOptions.map(({ value }) => value));
	}

	/* eslint-disable no-nested-ternary */
	select(values: (string | number)[], collapse?: boolean) {
		const newState: AdvancedSelectState = {
			expanded:
				typeof collapse === 'boolean'
					? !collapse
					: this.params.multiple
					? true
					: false,
			selectedOptions: this.params.options.filter(({ value }) =>
				values.includes(String(value))
			),
		};

		this.setState(newState);
	}

	onBodyClick(event: MouseEvent) {
		if (this.element.contains(event.target as HTMLElement)) {
			event.stopPropagation();
		} else {
			this.setState({ expanded: false });
		}
	}

	componentDidUpdate() {
		const { selectedOptions, expanded } = this.state;
		const selectedValues = selectedOptions.map(({ value }) => value);
		const selectedLabels = selectedOptions.map(({ label }) => label);

		if (expanded) {
			this.children?.list.setAttribute('aria-hidden', 'false');
		} else {
			this.children?.list.removeAttribute('aria-hidden');
		}

		this.children?.options.forEach((element: HTMLElement) => {
			if (
				selectedValues.includes(
					element.getAttribute('data-value') as string
				)
			) {
				element.setAttribute('aria-selected', 'true');
			} else {
				element.removeAttribute('aria-selected');
			}
		});

		if (this.children?.buttonLabel) {
			this.children?.button.setAttribute(
				'aria-expanded',
				expanded ? 'true' : 'false'
			);

			if (typeof this.params.onButtonLabelUpdate === 'function') {
				this.children.buttonLabel.textContent =
					this.params.onButtonLabelUpdate(this.state.selectedOptions);
			} else {
				this.children.buttonLabel.textContent = selectedLabels.length
					? selectedLabels.join(',')
					: 'Please select';
			}
		}
	}
}

export default AdvancedSelect;
